import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { firstValueFrom, map, Observable, take } from 'rxjs';

import { Functions } from '@angular/fire/functions';
import { httpsCallable } from 'firebase/functions';
import { NGXLogger } from 'ngx-logger';
import { AppException, AppExceptionModel } from '../models/app-exception.model';
import { Grade, GradeStatus } from '../models/grade.school.model';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class SchoolGradeService {
  http = inject(HttpClient);
  private functions = inject(Functions);
  afs: AngularFirestore = inject(AngularFirestore);
  logger: NGXLogger = inject(NGXLogger);
  utilsService = inject(UtilsService);

  constructor() {}

  getGradesByAcademicYear(
    groupId: string,
    academicYear: number,
    gradeStatus?: GradeStatus[],
  ): Promise<Grade[]> {
    const ref = this.afs
      .collection('groups')
      .doc(groupId)
      .collection('grades', (ref) => {
        const reference = ref.where('gradeYear', '==', academicYear);
        return reference.where(
          'gradeStatus',
          'in',
          gradeStatus ?? [GradeStatus.active],
        );
      });
    return firstValueFrom(
      ref.get().pipe(
        take(1),
        map((changes) => {
          return changes.docs.map((a) => {
            var data: Grade = a.data() as Grade;
            data.id = a.id;
            return data;
          });
        }),
      ),
    );
  }

  getAllGradesByGroupId(groupId: string): Promise<Grade[]> {
    this.logger.log(`getAllGradesByGroupId: ${groupId}`);
    const ref = this.afs.collection('groups').doc(groupId).collection('grades');
    return firstValueFrom(
      ref.get().pipe(
        take(1),
        map((changes) => {
          return changes.docs.map((a) => {
            var data: Grade = a.data() as Grade;
            data.id = a.id;
            return data;
          });
        }),
      ),
    );
  }

  getArchiveGradesByAcademicYear(
    groupId: string,
    academicYear: number,
  ): Promise<Grade[]> {
    this.logger.log(
      `getArchiveGradesByAcademicYear: ${groupId}, ${academicYear}`,
    );
    const ref = this.afs
      .collection('groups')
      .doc(groupId)
      .collection('grade_archive', (ref) => {
        const reference = ref.where('gradeStatus', '==', GradeStatus.active);
        return reference.where('gradeYear', '==', academicYear);
      });
    return firstValueFrom(
      ref.get().pipe(
        take(1),
        map((changes) => {
          return changes.docs.map((a) => {
            var data: Grade = a.data() as Grade;
            // Archive doc ids are {gradeId}_{year}; prefer the stored grade id.
            return { ...data, id: data.id ?? a.id };
          });
        }),
      ),
    );
  }

  getGradeById(groupId: string, id: string): Observable<Grade> {
    const doc = this.afs
      .collection('groups')
      .doc(groupId)
      .collection<Grade>('grades')
      .doc(id)
      .get();
    return doc.pipe(
      take(1),
      map((doc) => {
        return { ...doc.data(), id: id } as Grade;
      }),
    );
  }

  async create(grade: Grade): Promise<string> {
    // this.logger.log(`GradeService:: create: `, JSON.stringify(grade, null, 2));
    const doc = await this.afs
      .collection('groups')
      .doc(grade.groupId)
      .collection('grades')
      .add(grade);
    return doc.id;
  }

  async update(id: string, grade: Grade): Promise<void> {
    this.logger.log(
      `GradeService:: update: ${id}: `,
      JSON.stringify(grade, null, 2),
    );
    return this.afs
      .collection('groups')
      .doc(grade.groupId)
      .collection<Grade>('grades')
      .doc(id)
      .update(grade);
  }

  async gradeReassign(request: any): Promise<any> {
    try {
      const callable = httpsCallable(this.functions, 'gradeAssignTeacher', {
        timeout: 30000,
        limitedUseAppCheckTokens: true,
      });
      const result = await callable(request);
      return result.data;
    } catch (error) {
      throw new AppException({
        code: 'ERR_GRADE_ASSIGN',
        message: `Failed to assign grade. Please try again later. Detail error: ${error}`,
      });
    }
  }

  async delete(groupId: string, id: string): Promise<void> {
    return this.afs
      .collection('groups')
      .doc(groupId)
      .collection('grades')
      .doc(id)
      .delete();
  }
}
